import React, { useState } from "react";
import { Button } from "@/shared/ui/button"
import { Label } from "@/shared/ui/label"
import { Input } from "@/shared/ui/input"
import { Textarea } from "@/shared/ui/textarea"
import { Save, Trash2 } from "lucide-react";
import Loader from '@/shared/components/Loader';
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useDeleteSubstituteMutation,
  useUpdateSubstituteMutation,
} from "@/api/rtk/mutationsApi"

const DeleteButton = ({ id }) => {
  const [deleteSubstitute, { isLoading: deleting }] = useDeleteSubstituteMutation();

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteSubstitute({ id }).unwrap();
    } catch (error) {
      console.error("Error deleting substitute:", error);
      toast.error('Failed to delete substitute. Please try again.');
    }
  };

  return (
    <Button
      variant="outline"
      size="icon"
      onClick={handleDelete}
      disabled={deleting}
    >
      {
        deleting ? 
          <Loader variant='small'/> 
        : 
        <Trash2 className="w-4 h-4" />
      }

    </Button>
  );
}

export function SubstituteEditableRow({ item, mode }) {
  const { t } = useTranslation();
  const [find, setFind] = useState(item?.find || "");
  const [replace, setReplace] = useState(item?.replace || "");
  const [updateSubstitute, { isLoading: saving }] = useUpdateSubstituteMutation();
  const isVocabulary = mode === "vocabulary";

  if (!item) {
    return null;
  }

  const hasChanged = find?.trim() !== item.find || replace?.trim() !== item.replace;

  const handleSave = async () => {
    if (!item?.id) return;
    try {
      const updatedItem = {
        ...item,
        find: find?.trim() || "",
        replace: replace?.trim() || ""
      };
      await updateSubstitute(updatedItem).unwrap();
    } catch (error) {
      console.error("Error updating substitute:", error);
      toast.error('Failed to update substitute. Please try again.');
    }
  };

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="w-full">
        <div className="w-full">
          <div className={`flex gap-2 justify-center items-center h-16 border p-3 border-b-0 rounded-t-lg`}>
            <Label className={`text-gray-900 font-semibold text-sm ${isVocabulary ? 'min-w-[65px]' : ''}`}>{t('profile.find')}</Label>
            <Input 
              type="text" 
              value={find} 
              onChange={(e) => setFind(e.target.value)}
              className="w-full font-semibold bg-opacity-40 overflow-y-scroll h-full text-gray-600"
            />
          </div>
          {isVocabulary ? (
            <div className="flex gap-2 items-center h-16 border p-3 rounded-b-lg">
              <Label className="text-gray-900 font-semibold text-sm min-w-[65px]">{t('profile.replace')}</Label>
              <Input 
                type="text" 
                value={replace} 
                onChange={(e) => setReplace(e.target.value)}
                className="w-full font-semibold bg-opacity-40 overflow-y-scroll h-full text-gray-600"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 rounded-b-lg h-[170px] w-full border border-slate-200 bg-white p-3">
              <Label className="font-semibold text-gray-900">{t('profile.replace')}</Label>
              <Textarea 
                value={replace} 
                onChange={(e) => setReplace(e.target.value)}
                className="h-full overflow-y-auto font-semibold text-gray-600"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <DeleteButton id={item.id} />
        {hasChanged && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleSave}
            disabled={saving}
            className={`${hasChanged ? '' : 'opacity-50 cursor-not-allowed'}`}
          >
            {saving ? (
              <Loader variant='small'/>
            ) : (
              <Save className="w-4 h-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
